// PeakEdu Tier 0 WASM — Audio recording consent banner (B+ SD44 PM extension).
//
// Differentiation từ B18 cookie consent:
//   - Cookie consent: page-level, 12-month TTL, governs analytics/marketing
//   - Audio consent (this): ACTION-LEVEL, explicit per first record session,
//     governs sinh trắc học data (NĐ 13/2023 Art 26 + GDPR Art 9 special category)
//
// Compliance:
//   - NĐ 13/2023 Art 26 — sinh trắc học data đặc biệt yêu cầu explicit consent
//   - GDPR Art 9(2)(a) — explicit consent cho special category processing
//   - Privacy Policy § 9 (audio recording đặc thù)
//
// UX flow:
//   1. User click "🎤 Bắt đầu ghi âm" trong main.js
//   2. main.js gọi await ensureAudioConsent() trước getUserMedia()
//   3. Nếu chưa có consent → show modal banner
//      - User accept → persist localStorage + return Promise<true>
//      - User decline → return Promise<false> → main.js abort recording
//   4. Subsequent recording sessions → check localStorage skip banner
//   5. User can revoke qua "Cài đặt cookie" footer link (resets all consents)
//
// Storage: localStorage key 'peakedu_audio_consent':
//   {
//     version: "1.0",
//     timestamp: "2026-05-07T12:00:00.000Z",
//     accepted: true,
//     scope: "alpha_beta",
//     opt_in_train_ai: false   // Default OFF, user explicit opt-in nếu muốn
//   }

const STORAGE_KEY = 'peakedu_audio_consent';
const CURRENT_VERSION = '1.0';
const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Read audio consent state.
 * Returns null nếu chưa có hoặc parse fail hoặc expired.
 */
export function getAudioConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed.accepted !== true) return null;
    if (parsed.version !== CURRENT_VERSION) return null;
    // Check 12-month TTL
    const ts = new Date(parsed.timestamp).getTime();
    if (!Number.isFinite(ts)) return null;
    if (Date.now() - ts > TWELVE_MONTHS_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist audio consent với explicit accepted=true.
 * @param {object} opts
 * @param {boolean} opts.optInTrainAi — default false (only train AI nếu user opt-in explicit)
 * @param {string} opts.scope — default "alpha_beta"
 */
export function setAudioConsent({ optInTrainAi = false, scope = 'alpha_beta' } = {}) {
  const record = {
    version: CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    accepted: true,
    scope,
    opt_in_train_ai: Boolean(optInTrainAi),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Private browsing / quota exceeded — silent fail
  }
  return record;
}

/**
 * Clear audio consent (boss "Cài đặt cookie" reset trigger this too).
 */
export function resetAudioConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Show audio consent modal + return Promise<boolean> based user choice.
 * Resolves true nếu user accept, false nếu decline.
 *
 * @param {object} opts
 * @param {string} opts.legalBaseUrl — Base URL legal pages (vd "https://peakedu.vn")
 * @returns {Promise<boolean>}
 */
function showConsentModal({ legalBaseUrl }) {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.id = 'peakedu-audio-consent-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'audio-consent-title');
    root.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(26, 54, 93, 0.85);
      display: flex; align-items: center; justify-content: center;
      z-index: 100000;
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    const privacyUrl = `${legalBaseUrl.replace(/\/$/, '')}/chinh-sach-bao-mat/`;

    root.innerHTML = `
      <div style="background: #ffffff; max-width: 540px; width: 100%; border-radius: 12px; overflow: hidden; box-shadow: 0 8px 32px rgba(0,0,0,0.2);">
        <div style="background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%); padding: 20px 24px;">
          <h2 id="audio-consent-title" style="margin: 0; color: #fff; font-size: 20px; font-weight: 700;">
            🎤 Đồng ý ghi âm giọng nói
          </h2>
        </div>
        <div style="padding: 20px 24px; color: #2d3748; font-size: 15px; line-height: 1.6;">
          <p style="margin: 0 0 12px 0;">
            PeakEdu cần <strong>ghi âm giọng nói</strong> của bạn để hệ thống AI chấm điểm phát âm.
          </p>
          <p style="margin: 0 0 12px 0;">
            <strong>Chúng tôi cam kết:</strong>
          </p>
          <ul style="margin: 0 0 16px 0; padding-left: 20px; font-size: 14px; color: #4a5568;">
            <li>Audio xử lý bởi AI tự động — <strong>không ai nghe trực tiếp</strong></li>
            <li>Audio <strong>xoá tự động sau 24 giờ</strong> (trừ khi bạn opt-in để giúp train AI)</li>
            <li>KHÔNG dùng cho Speaker ID / Voice Authentication</li>
            <li>Mã hoá tại nghỉ + truyền tải</li>
          </ul>
          <div style="background: #ebf4ff; border-left: 3px solid #3182ce; padding: 10px 12px; border-radius: 4px; margin-bottom: 16px;">
            <label style="display: flex; align-items: flex-start; gap: 8px; cursor: pointer; font-size: 14px; color: #2c5282;">
              <input type="checkbox" id="audio-consent-train-ai" style="margin-top: 3px; cursor: pointer;" />
              <span>
                <strong>Tuỳ chọn nâng cao:</strong> Tôi đồng ý để PeakEdu <strong>sử dụng audio của tôi giúp cải tiến AI</strong> (lưu 36 tháng, có thể rút lại bất kỳ lúc nào)
              </span>
            </label>
          </div>
          <p style="margin: 0 0 16px 0; font-size: 13px; color: #718096;">
            Chi tiết: <a href="${privacyUrl}" target="_blank" rel="noopener noreferrer" style="color: #3182ce; text-decoration: underline;">Chính sách bảo mật § 9</a> (audio recording đặc thù)
          </p>
          <div style="display: flex; gap: 12px; justify-content: flex-end; flex-wrap: wrap;">
            <button type="button" id="audio-consent-decline"
                    style="padding: 10px 20px; background: transparent; color: #4a5568; border: 1px solid #cbd5e0; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500;">
              Từ chối
            </button>
            <button type="button" id="audio-consent-accept"
                    style="padding: 10px 24px; background: #3182ce; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600;">
              Đồng ý ghi âm
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(root);

    function dismiss(accepted, optInTrainAi) {
      try {
        root.remove();
      } catch {
        /* ignore */
      }
      resolve({ accepted, optInTrainAi });
    }

    root.querySelector('#audio-consent-accept').addEventListener('click', () => {
      const optInTrainAi = root.querySelector('#audio-consent-train-ai')?.checked || false;
      dismiss(true, optInTrainAi);
    });

    root.querySelector('#audio-consent-decline').addEventListener('click', () => {
      dismiss(false, false);
    });

    // Click backdrop = decline
    root.addEventListener('click', (e) => {
      if (e.target === root) dismiss(false, false);
    });

    // ESC key = decline
    const onKey = (e) => {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', onKey);
        dismiss(false, false);
      }
    };
    document.addEventListener('keydown', onKey);
  });
}

/**
 * Ensure audio consent before recording. Show modal nếu chưa có valid consent.
 *
 * @param {object} opts
 * @param {string} opts.legalBaseUrl — VITE_LEGAL_BASE_URL
 * @returns {Promise<boolean>} true nếu user accepted (now or previously), false nếu decline
 */
export async function ensureAudioConsent({ legalBaseUrl = 'https://peakedu.vn' } = {}) {
  const existing = getAudioConsent();
  if (existing) {
    return true;
  }
  const result = await showConsentModal({ legalBaseUrl });
  if (result.accepted) {
    setAudioConsent({ optInTrainAi: result.optInTrainAi });
    return true;
  }
  return false;
}

export default {
  ensure: ensureAudioConsent,
  get: getAudioConsent,
  set: setAudioConsent,
  reset: resetAudioConsent,
};
